import { inject, injectable } from "inversify";
import type { ApiResponse } from "../models/response.model";
import axios, { isAxiosError } from "axios";
import TokenRepo from "./token.repo";

const BASE_URL = process.env.REACT_APP_API_URL;
const NGROK = (process.env.REACT_APP_API_NGROK === "true"?{"ngrok-skip-browser-warning": "true"}:{})

function logApiError(err: unknown) {
  if (isAxiosError(err) && err.response?.status === 401) {
    return;
  }
  console.error(err);
}

// Credential requests must never log Axios config, which contains the password.
function sensitiveApiError<T>(err: unknown): ApiResponse<T> | undefined {
  if (!isAxiosError(err)) return undefined;
  const data = err.response?.data;
  if (!data || typeof data !== "object" || typeof data.code !== "string" || data.code === "00" ||
      (typeof data.errorMessage !== "string" && data.errorMessage !== null)) return undefined;
  return { code: data.code, errorMessage: data.errorMessage, result: null as T };
}

function isFieldErrors(value: unknown): value is Record<string, string[]> {
  return !!value && typeof value === "object" && !Array.isArray(value) &&
    Object.values(value).every(messages => Array.isArray(messages) && messages.every(message => typeof message === "string"));
}

function readErrorEnvelope<T>(value: unknown): ApiResponse<T | null> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const data = value as Record<string, unknown>;
  if (typeof data.code !== "string" || !data.code || !("result" in data) ||
      (typeof data.errorMessage !== "string" && data.errorMessage !== null)) return undefined;
  return {
    code: data.code,
    errorMessage: data.errorMessage,
    result: data.result as T | null,
    ...(typeof data.errorCode === "string" ? { errorCode: data.errorCode } : {}),
    ...(isFieldErrors(data.errors) ? { errors: data.errors } : {}),
  };
}

@injectable()
export default class ApiRepo {

  @inject(TokenRepo)
  private tokenRepo!: TokenRepo;

  async get<T>(path: string, params?: object, useToken = true, sensitive = false): Promise<ApiResponse<T> | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token&&useToken) {
        result = await axios.get(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + token,
          ...NGROK
        }});
      } else {
        result = await axios.get(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json", ...NGROK}});
      }
      return result.data as ApiResponse<T>;
    } catch (err) {
      if (sensitive) return sensitiveApiError<T>(err);
      logApiError(err);
    }
  }

  async getBlob(path: string, params?: object, useToken = true): Promise<string | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token&&useToken) {
        result = await axios.get(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token}, responseType: "blob"},)
      } else {
        result = await axios.get(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json"}, responseType: "blob"});
      }
      return result.data;
    } catch (err) {
      logApiError(err);
    }
  }

  async post<T>(path: string, body?: object, params?: object, useToken = true, sensitive = false): Promise<ApiResponse<T> | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token&&useToken) {
        result = await axios.post(BASE_URL + path, body, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token}})
      } else {
        result = await axios.post(BASE_URL + path, body, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json"}})
      }
      return result.data as ApiResponse<T>;
    } catch (err) {
      if (sensitive) return sensitiveApiError<T>(err);
      logApiError(err);
    }
  }

  async postWithErrors<T>(path: string, body?: object, params?: object, useToken = true): Promise<ApiResponse<T | null>> {
    try {
      const token = useToken ? this.tokenRepo.getToken() : null;
      const response = await axios.post(BASE_URL + path, body, {
        params: { v: "1.0", ...params },
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: "Bearer " + token } : {}),
        },
      });
      return readErrorEnvelope<T>(response.data) ?? {
        code: "99", errorCode: "INVALID_RESPONSE", errorMessage: "Phản hồi từ máy chủ không hợp lệ. Vui lòng thử lại.", result: null,
      };
    } catch (error) {
      // Only inspect the response; Axios requests/config may contain credentials.
      if (isAxiosError(error) && error.response) {
        const { status, data } = error.response;
        const envelope = readErrorEnvelope<T>(data);
        if (envelope && envelope.code !== "00") return envelope;
        if (status === 401) return {
          code: "04", errorCode: "AUTH_REQUIRED", errorMessage: "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.", result: null,
        };
        if (status === 403) return {
          code: "04", errorCode: "ACCESS_DENIED", errorMessage: "Bạn không có quyền thực hiện thao tác này.", result: null,
        };
        if (status === 400) return {
          code: "01", errorCode: "HTTP_ERROR", errorMessage: "Dữ liệu không hợp lệ. Vui lòng kiểm tra lại thông tin.", result: null,
        };
        return { code: "99", errorCode: "HTTP_ERROR", errorMessage: "Không thể thực hiện yêu cầu. Vui lòng thử lại.", result: null };
      }
      return { code: "99", errorCode: "NETWORK_ERROR", errorMessage: "Không thể kết nối máy chủ. Vui lòng kiểm tra kết nối và thử lại.", result: null };
    }
  }

  async postMultipart<T>(path: string, formData: FormData, params?: object, useToken = true): Promise<ApiResponse<T> | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token && useToken) {
        result = await axios.post(BASE_URL + path, formData, {
          params: { v: "1.0", ...params },
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": "Bearer " + token
          }
        });
      } else {
        result = await axios.post(BASE_URL + path, formData, {
          params: { v: "1.0", ...params },
          headers: {
            "Content-Type": "multipart/form-data"
          }
        });
      }
      return result.data as ApiResponse<T>;
    } catch (err) {
      logApiError(err);
    }
  }

  async patch<T>(path: string, body?: object, params?: object, useToken = true): Promise<ApiResponse<T> | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token&&useToken) {
        result = await axios.patch(BASE_URL + path, body, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token}})
      } else {
        result = await axios.patch(BASE_URL + path, body, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json"}})
      }
      return result.data as ApiResponse<T>;
    } catch (err) {
      logApiError(err);
    }
  }

  async delete<T>(path: string, params?: object, useToken = true): Promise<ApiResponse<T> | undefined> {
    try {
      const token = this.tokenRepo.getToken();
      let result;
      if (token&&useToken) {
        result = await axios.delete(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token}})
      } else {
        result = await axios.delete(BASE_URL + path, {params: {v: "1.0", ...params}, headers: {"Content-Type": "application/json"}})
      }
      return result.data as ApiResponse<T>;
    } catch (err) {
      logApiError(err);
    }
  }
}
